import {  Input } from "@storybook";
import { useRecoilState } from "recoil";
import { EMBEDED_FORM, EmbededFormState } from "./stores/state";
import { useEffect, useRef, useCallback } from "react";

interface ICombinedInput {
  handleChange?: any;
}
export const CombinedInput = ({ handleChange }: ICombinedInput) => {
  const [form, setForm] = useRecoilState(EmbededFormState);
  const cursorPositionRef = useRef<number | null>(null);
  const shouldRestoreCursor = useRef<boolean>(false);
  const formatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRawValueRef = useRef<string>("");
  const isUserTypingRef = useRef<boolean>(false);
  const lastFormattedValueRef = useRef<string>("");
  const isPhone = (value: string) => {
    // If it contains @ symbol, it's definitely an email
    if (value.includes("@")) {
      return false;
    }
    
    // Remove common phone formatting characters and check if mostly digits
    const digitsOnly = value.replace(/[\s\-()+.]/g, "");
    const hasEnoughDigits = digitsOnly.length >= 3; // Minimum for a phone number
    const isAllDigits = /^\d+$/.test(digitsOnly);
    
    // If it has phone formatting characters like (), -, spaces, +
    const hasPhoneFormatting = /[()\-\s+]/.test(value);
    
    return isAllDigits && hasEnoughDigits && (hasPhoneFormatting || digitsOnly.length >= 3);
  };
  const clean = (value: string) => value.replace(/\s+/g, "");





  // Calculate precise cursor position after formatting
  const calculateCursorPosition = useCallback((
    originalValue: string,
    formattedValue: string,
    originalCursorPos: number
  ): number => {
    // If cursor is at the end, keep it at the end
    if (originalCursorPos >= originalValue.length) {
      return formattedValue.length;
    }

    // Count digits before cursor in original value
    const digitsBefore = originalValue.slice(0, originalCursorPos).replace(/\D/g, "").length;
    
    // Find position in formatted value where we have the same number of digits
    let digitsFound = 0;
    let newPosition = 0;
    
    for (let i = 0; i < formattedValue.length; i++) {
      const char = formattedValue[i];
      if (char && /\d/.test(char)) {
        digitsFound++;
        if (digitsFound === digitsBefore) {
          newPosition = i + 1;
          break;
        }
      }
    }
    
    // If we didn't find enough digits, place cursor at end
    if (digitsFound < digitsBefore) {
      return formattedValue.length;
    }
    
    return Math.min(Math.max(0, newPosition), formattedValue.length);
  }, []);

  // Smart phone input handler that respects user intent
  const handlePhoneInput = useCallback((
    rawValue: string,
    label: string,
    countryCode?: string,
    cursorPosition?: number,
    forceFormat = false
  ) => {
    // Clear any existing timeout
    if (formatTimeoutRef.current) {
      clearTimeout(formatTimeoutRef.current);
    }

    // Extract digits only for comparison
    const currentDigits = rawValue.replace(/\D/g, "");
    const lastDigits = lastRawValueRef.current.replace(/\D/g, "");
    const lastFormattedDigits = lastFormattedValueRef.current.replace(/\D/g, "");

    // Detect if user is deleting digits (important for preventing auto-restore)
    const isDeleting = currentDigits.length < lastDigits.length || 
                      currentDigits.length < lastFormattedDigits.length;

    // Store current raw value and cursor position
    lastRawValueRef.current = rawValue;
    const storedCursorPos = cursorPosition || 0;
    cursorPositionRef.current = storedCursorPos;

    const currentCountryCode = countryCode || form?.countryCode || "+1";

    // Create immediate form (unformatted during typing, unless forced)
    let displayValue = rawValue;
    let calculatedCursorPos = storedCursorPos;

    // Only format if forced (like country code change or timeout)
    if (forceFormat) {
      if (EMBEDED_FORM.numberFormatter && currentDigits.length > 0) {
        const formattedData = EMBEDED_FORM.numberFormatter({
          value: rawValue,
          countryCode: currentCountryCode,
        });
        const newFormattedValue = formattedData.formatted ?? rawValue;
        
        // Calculate new cursor position based on formatting change
        calculatedCursorPos = calculateCursorPosition(rawValue, newFormattedValue, storedCursorPos);
        
        displayValue = newFormattedValue;
        lastFormattedValueRef.current = displayValue;
        
        // Store the calculated cursor position for restoration
        cursorPositionRef.current = calculatedCursorPos;
        shouldRestoreCursor.current = true;
        
        // Mark as no longer typing after formatting
        isUserTypingRef.current = false;
      }
    } else {
      // Mark as user typing when not forcing format
      isUserTypingRef.current = true;
    }

    const newForm = {
      ...form,
      value: displayValue,
      isCountryCodeVisible: true,
      name: label,
      countryCode: currentCountryCode,
    };

    // Validate - smart validation for phone numbers
    if (EMBEDED_FORM.numberValidation && label === "phone") {
      // Extract digits only from the raw input for validation
      const digitsOnly = rawValue.replace(/\D/g, "");
      
      // Smart validation: Only validate meaningful input (7+ digits)
      if (digitsOnly.length === 0) {
        // Empty phone number - no validation error
        delete newForm.isError;
        newForm.errorMessage = "";
      } else if (digitsOnly.length > 0 && digitsOnly.length < 7) {
        // User still typing - don't validate until they have meaningful input (7+ digits)        // delete newForm.isError;
        newForm.errorMessage = "";
      } else {
        // Only validate when user has 7+ digits (more meaningful)
        const { isError, errorMessage } = EMBEDED_FORM.numberValidation(digitsOnly);
        newForm.isError = isError;
        newForm.errorMessage = errorMessage;
        
        // eslint-disable-next-line no-console
        console.log("🔍 Combined input validating digits:", digitsOnly, "- Error:", isError, errorMessage);
      }
    }



    handleChange?.(newForm);
    setForm(newForm);

    // Set timeout for formatting only if this is not a forced format and user is adding content (not deleting)
    if (!forceFormat && !isDeleting && currentDigits.length >= 3) {
      // eslint-disable-next-line no-console
      console.log("⏱️ Setting 2-second timer for:", rawValue);
      formatTimeoutRef.current = setTimeout(() => {
        // Double-check the user stopped typing and value hasn't changed
        if (lastRawValueRef.current === rawValue && isUserTypingRef.current) {
          // eslint-disable-next-line no-console
          console.log("📱 Auto-formatting after 2 seconds:", rawValue);
          handlePhoneInput(rawValue, label, countryCode, storedCursorPos, true);
        } else {
          // eslint-disable-next-line no-console
          console.log("❌ Timeout cancelled - value changed or no longer typing");
        }
      }, 2000);
    }
  }, [form, handleChange, setForm, calculateCursorPosition]);

  const handleEmailInput = (cleanedValue: string, label: string) => {
    const newForm = {
      ...form,
      value: cleanedValue,
      isCountryCodeVisible: false,
      name: label,
    };
    if (EMBEDED_FORM.emailValidation) {
      const { isError, errorMessage } =
        EMBEDED_FORM.emailValidation(cleanedValue);
      newForm.isError = isError;
      newForm.errorMessage = errorMessage;
    }
    handleChange?.(newForm);

    setForm(() => {
      return newForm;
    });
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = e.target;
    const cleanedValue = clean(value);
    const label = isPhone(cleanedValue) ? "phone" : "email";
    
    if (label === "phone") {
      // Always use smart phone handler for phone numbers
      handlePhoneInput(value, label, undefined, selectionStart || 0);
    } else {
      // Immediate update for email
      handleEmailInput(cleanedValue, label);
      // Reset phone-specific refs when switching to email
      isUserTypingRef.current = false;
      lastRawValueRef.current = "";
      lastFormattedValueRef.current = "";
    }
  };

  useEffect(() => {
    if (form.value) {
      handleChange?.(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup timeout on unmount and reset typing state on blur
  useEffect(() => {
    const handleBlur = () => {
      // On blur, format immediately if user was typing
      if (isUserTypingRef.current && lastRawValueRef.current) {
        isUserTypingRef.current = false;
        handlePhoneInput(lastRawValueRef.current, form.name || "phone", form.countryCode, undefined, true);
      }
    };

    const inputElement = document.querySelector('.input__field') as HTMLInputElement;
    if (inputElement) {
      inputElement.addEventListener('blur', handleBlur);
    }

    return () => {
      if (formatTimeoutRef.current) {
        clearTimeout(formatTimeoutRef.current);
      }
      if (inputElement) {
        inputElement.removeEventListener('blur', handleBlur);
      }
    };
  }, [form.name, form.countryCode, handlePhoneInput]);

  // Effect to restore cursor position after form state update
  useEffect(() => {
    if (shouldRestoreCursor.current && cursorPositionRef.current !== null) {
      const input = document.querySelector('.input__field') as HTMLInputElement;
      if (input && input.value) {
        setTimeout(() => {
          const cursorPos = cursorPositionRef.current || 0;
          // Simply set the cursor to the same position, clamped to value length
          const finalPos = Math.min(cursorPos, input.value.length);
          input.setSelectionRange(finalPos, finalPos);
          shouldRestoreCursor.current = false;
          cursorPositionRef.current = null;
        }, 0);
      }
    }
  }, [form.value]);



  return (
    <Input
      {...form}
      handleChange={handleChangeInput}
      handleChangeCountry={(e: any) =>
        handlePhoneInput(form.value as any, form.name, e.label || e.value || e, undefined, true)
      }
    />
  );
};
